import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, Clock, MapPin, Facebook, Linkedin, Youtube, Instagram, Twitter, ArrowRight, Star, Award, ShieldCheck, Send, Loader2, Building2 } from "lucide-react";
import { publicApi } from "../../lib/api";
import { openCookieSettings } from "../CookieConsent";
import { useToast } from "../../hooks/use-toast";
import logoLight from "../../assets/cass-logo-light.png";
import xeroPlatinumWhite from "../../assets/xero-capsule-whiteout.webp";
import icbCrest from "../../assets/icb-crest.png";
import GoogleRatingBadge from "../GoogleRatingBadge";
import { LOCATIONS, PRIMARY_LOCATION_SLUGS } from "../../data/locations";
import { BUSINESS_HOURS_LABEL } from "../../lib/business";
import useAskCassieStatus from "../../hooks/useAskCassieStatus";

const SOCIAL_ICONS = { linkedin: Linkedin, facebook: Facebook, youtube: Youtube, instagram: Instagram, twitter: Twitter, x: Twitter };

export default function Footer() {
  const { toast } = useToast();
  const [footer, setFooter] = useState(null);
  const [reviewUrl, setReviewUrl] = useState("");
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);
  const askCassie = useAskCassieStatus();

  useEffect(() => {
    publicApi.footer().then(setFooter).catch(() => {});
    publicApi.googleRating().then((d) => setReviewUrl(d?.write_review_url || "")).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    setSubscribing(true);
    try {
      await publicApi.subscribe({ name: "", email });
      toast({ title: "You're subscribed!", description: "Thanks — we'll be in touch with useful tips." });
      setEmail("");
    } catch {
      toast({ title: "Couldn't subscribe", description: "Please check your email and try again.", variant: "destructive" });
    } finally { setSubscribing(false); }
  };

  if (!footer) return <footer className="bg-navy-deep h-40" />;
  const ch = footer.companies_house || {};
  const socials = (footer.socials || []).filter((s) => s.url);

  return (
    <footer className="bg-navy-deep text-slate-300">
      {/* CTA strip */}
      <div className="bg-orange">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <h3 className="font-display font-extrabold text-2xl md:text-3xl text-white text-center md:text-left">Let's talk about your business</h3>
          <Link to="/contact" className="inline-flex items-center gap-2 bg-white text-navy font-bold text-sm px-7 py-3.5 rounded-full hover:shadow-xl transition-all hover:-translate-y-0.5">LET'S TALK <ArrowRight size={16} /></Link>
        </div>
      </div>

      {/* Newsletter band */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-8 flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="text-center md:text-left">
            <p className="font-display font-bold text-white text-lg">{footer.newsletter?.heading}</p>
            <p className="text-sm text-slate-400">{footer.newsletter?.text}</p>
          </div>
          <form onSubmit={subscribe} className="flex w-full md:w-auto gap-2" data-testid="footer-newsletter-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@business.co.uk" data-testid="footer-newsletter-email" className="flex-1 md:w-72 rounded-full bg-white/10 border border-white/15 px-5 py-3 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange/50" />
            <button type="submit" disabled={subscribing} data-testid="footer-newsletter-submit" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold text-sm px-6 py-3 rounded-full transition-all shrink-0">
              {subscribing ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Sign up
            </button>
          </form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
        {/* Brand */}
        <div>
          <img loading="lazy" decoding="async" src={logoLight} alt="Cloud Accounting Support Services" className="h-16 w-auto mb-4" />
          <p className="text-sm leading-relaxed mb-5">{footer.tagline}</p>
          {socials.length > 0 && (
            <div className="flex gap-3 mb-5">
              {socials.map((s) => {
                const Icon = SOCIAL_ICONS[(s.platform || "").toLowerCase()] || Linkedin;
                return (
                  <a key={s.platform} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.platform} data-testid={`social-${s.platform.toLowerCase()}`} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-orange transition-colors">
                    <Icon size={16} className="text-white" />
                  </a>
                );
              })}
            </div>
          )}
          <GoogleRatingBadge dark />
          {reviewUrl && (
            <a href={reviewUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-leave-review" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-300 hover:text-orange transition-colors">
              <Star size={13} className="text-orange" fill="#F5821F" /> Leave us a review
            </a>
          )}
        </div>

        {/* Popular services */}
        <div>
          <h4 className="font-display font-bold text-white mb-4">Popular Services</h4>
          <ul className="space-y-1 text-sm [&_a]:inline-block [&_a]:py-1.5">
            {(footer.quick_links || []).map((l) => (
              <li key={l.url + l.label}><Link to={l.url} className="hover:text-orange transition-colors">{l.label}</Link></li>
            ))}
          </ul>
        </div>

        {/* Company */}
        <div>
          <h4 className="font-display font-bold text-white mb-4">Company</h4>
          <ul className="space-y-1 text-sm [&_a]:inline-block [&_a]:py-1.5">
            <li><Link to="/about" className="hover:text-orange transition-colors">About Us</Link></li>
            <li><Link to="/careers" data-testid="footer-careers" className="hover:text-orange transition-colors">Careers</Link></li>
            <li><Link to="/switching-accountants" data-testid="footer-switching" className="hover:text-orange transition-colors">Switching to CASS</Link></li>
            <li><Link to="/business-health-check" data-testid="footer-health-check" className="hover:text-orange transition-colors">Business Growth Health Check</Link></li>
            <li><Link to="/key-tax-dates" data-testid="footer-tax-dates" className="hover:text-orange transition-colors">Key Tax Dates</Link></li>
            <li><Link to="/legal-cashiering" data-testid="footer-legal-cashiering" className="hover:text-orange transition-colors">Legal Cashiering</Link></li>
            <li><Link to="/insights" className="hover:text-orange transition-colors">Insights</Link></li>
            <li><Link to="/podcast" className="hover:text-orange transition-colors">Podcast</Link></li>
            <li><Link to="/testimonials" className="hover:text-orange transition-colors">Testimonials</Link></li>
            <li>
              <Link to={askCassie.faqs_retired ? "/ask-cassie" : "/faqs"} data-testid="footer-answers" className="hover:text-orange transition-colors">
                {askCassie.faqs_retired ? "Ask Cassie" : "FAQs"}
              </Link>
            </li>
            <li><Link to="/contact" className="hover:text-orange transition-colors">Contact</Link></li>
          </ul>
        </div>

        {/* Contact + accreditations */}
        <div>
          <h4 className="font-display font-bold text-white mb-4">Get in touch</h4>
          <ul className="space-y-3 text-sm mb-6">
            {footer.contact?.phone && <li className="flex items-center gap-2.5"><Phone size={15} className="text-orange" /> {footer.contact.phone}</li>}
            {footer.contact?.email && <li className="flex items-center gap-2.5"><Mail size={15} className="text-orange" /> {footer.contact.email}</li>}
            <li className="flex items-center gap-2.5"><Clock size={15} className="text-orange" /> {BUSINESS_HOURS_LABEL}</li>
            {footer.contact?.address && <li className="flex items-start gap-2.5"><MapPin size={15} className="text-orange mt-0.5 shrink-0" /> <span className="leading-relaxed">{footer.contact.address}</span></li>}
          </ul>
          <div className="flex flex-wrap gap-2">
            {(footer.badges || []).map((b) => (
              <span key={b.label} className="inline-flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1.5 text-xs font-semibold text-white" data-testid="footer-badge"><ShieldCheck size={12} className="text-orange" /> {b.label}</span>
            ))}
            {(footer.awards || []).map((a) => (
              <span key={a.label} className="inline-flex items-center gap-1.5 bg-white/10 rounded-full px-3 py-1.5 text-xs font-semibold text-white" data-testid="footer-award"><Award size={12} className="text-orange" /> {a.label}</span>
            ))}
          </div>
          <a href="https://www.xero.com/uk/advisors/accountant/" target="_blank" rel="noopener noreferrer" data-testid="footer-xero-badge" className="mt-5 inline-block">
            <img loading="lazy" decoding="async" src={xeroPlatinumWhite} alt="Xero Platinum Partner" className="h-12 w-auto opacity-95 hover:opacity-100 transition-opacity" />
          </a>
          <div className="mt-5 flex items-center gap-3" data-testid="footer-icb">
            <div className="bg-white rounded-xl p-2 flex items-center justify-center shrink-0">
              <img loading="lazy" decoding="async" src={icbCrest} alt="The Institute of Certified Bookkeepers" className="h-14 w-auto" />
            </div>
            <p className="text-xs text-slate-400 leading-snug">Regulated by the Institute of Certified Bookkeepers<br /><span className="text-slate-300 font-semibold">Practice Licence No. ICB101860</span></p>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 pt-6 pb-2 text-xs text-slate-400 leading-relaxed space-y-1.5">
          <p><span className="text-slate-300 font-semibold">Areas we serve:</span>{" "}
            {PRIMARY_LOCATION_SLUGS.map((slug) => LOCATIONS.find((l) => l.slug === slug)).filter(Boolean).map((l, i, arr) => (
              <React.Fragment key={l.slug}>
                <Link to={`/${l.slug}`} className="hover:text-orange transition-colors underline-offset-2 hover:underline" data-testid={`footer-area-${l.slug.replace("accountants-", "")}`}>{l.town}</Link>{i < arr.length - 1 ? ", " : ""}
              </React.Fragment>
            ))}
            {" "}— and across the UK through cloud accounting.{" "}
            <Link to="/areas-we-support" className="text-slate-300 font-semibold hover:text-orange transition-colors underline-offset-2 hover:underline" data-testid="footer-areas-hub">See all areas we support</Link>.
          </p>
          {(ch.company_name || ch.number) && (
            <p className="flex items-start gap-1.5" data-testid="footer-companies-house"><Building2 size={13} className="text-slate-500 mt-0.5 shrink-0" />
              <span>{ch.company_name}{ch.number ? ` · Company No. ${ch.number}` : ""}{ch.vat ? ` · VAT ${ch.vat}` : ""}{ch.registered_office ? ` · Registered office: ${ch.registered_office}` : ""}</span>
            </p>
          )}
        </div>
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-slate-400 border-t border-white/5">
          <div className="flex flex-col md:flex-row items-center gap-1.5 md:gap-3 text-center md:text-left">
            <p>© {new Date().getFullYear()} Cloud Accounting Support Services. All rights reserved.</p>
            <p data-testid="footer-cass-group">Part of{" "}
              <a href="https://thecassgroup.co.uk" target="_blank" rel="noopener noreferrer" className="text-slate-300 font-semibold hover:text-orange transition-colors underline-offset-2 hover:underline">The CASS Group</a>
            </p>
          </div>
          <div className="flex gap-5">
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="hover:text-orange transition-colors">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-orange transition-colors">Terms &amp; Conditions</Link>
            <button
              type="button"
              onClick={openCookieSettings}
              data-testid="footer-cookie-settings"
              className="hover:text-orange transition-colors"
            >
              Cookie settings
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
